import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import SEO from "@/components/SEO";

// Persistent dedupe for the Lead conversion: an in-memory Set covers React.StrictMode's
// dev double-effect, and sessionStorage covers accidental page refreshes on the
// thank-you page (browsers preserve history.state across reloads).
const SESSION_KEY = "928_fired_lead_ids";
const firedLeadSubmissionIds = new Set(
  (() => {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]");
    } catch {
      return [];
    }
  })()
);
function markLeadFired(id) {
  firedLeadSubmissionIds.add(id);
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify([...firedLeadSubmissionIds])); } catch { /* ignore */ }
}

export default function MockupThankYou() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const fromSubmit = Boolean(state.fromSubmit);
  const submissionId = state.submissionId;

  useEffect(() => {
    if (!fromSubmit || !submissionId) return;
    if (firedLeadSubmissionIds.has(submissionId)) return;
    markLeadFired(submissionId);
    trackEvent("mockup_form_submit", {
      service: state.service || "not_provided",
      vehicle: state.vehicle || "not_provided",
      has_business: Boolean(state.has_business),
      source: state.source || "not_provided",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="page-mockup-thank-you" className="pt-32 pb-32 bg-[var(--bg-2)] min-h-screen">
      <SEO
        title="Thank you — 928 Wraps"
        description="Your mockup request has been received. Dave will personally review it and reply within 1 business day."
        noindex
      />

      <div className="wrap">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-[var(--red)] text-white grid place-items-center mx-auto shadow-lg shadow-red-500/20">
            <Check size={36} strokeWidth={2.5} />
          </div>

          <div className="eyebrow eyebrow-red mt-8">Mockup request received</div>
          <h1 data-testid="thank-you-heading" className="h-display text-5xl md:text-7xl uppercase mt-4">
            You&apos;re <span className="italic font-normal">in.</span>
          </h1>

          <p className="mt-6 text-lg text-neutral-600 max-w-xl mx-auto leading-relaxed">
            Dave just got your details. Expect a text or email within <span className="text-neutral-900 font-semibold">1 business day</span> — no pressure, no runaround.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
            {[
              { n: "01", t: "Dave reviews it", d: "Personally. Every request." },
              { n: "02", t: "You get a mockup", d: "Real vehicle. Real brand. Real result." },
              { n: "03", t: "You decide", d: "No pressure to move forward." },
            ].map((s) => (
              <div key={s.n} className="p-6 bg-white border border-neutral-200 rounded-2xl">
                <div className="font-mono text-xs tracking-widest text-[var(--red)]">{s.n}</div>
                <div className="font-semibold mt-3">{s.t}</div>
                <div className="text-sm text-neutral-500 mt-1">{s.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/portfolio" data-testid="thank-you-portfolio" className="btn-ghost">
              See recent work <ArrowRight size={16} />
            </Link>
            <Link to="/" data-testid="thank-you-home" className="btn-dark">
              Back home <ArrowRight size={16} />
            </Link>
          </div>

          <p className="mt-10 text-xs text-neutral-500">
            Didn&apos;t mean to submit? <button onClick={() => navigate("/mockup")} className="underline hover:text-neutral-900">Go back to the form</button>.
          </p>
        </div>
      </div>
    </div>
  );
}
